<template>
    <div>
        <div id="exportable-content">
            <Hero title="The evolution of API integration platforms"
                :subtitle="`From the 1970s to ${new Date().getFullYear()} — here's how integration technology has evolved and why engineers are choosing Unified.to to power integrations for AI and SaaS today.`"
                :highlight-words="['integration']" :buttons="[
                    {
                        link: 'https://app.unified.to',
                        title: 'Get started free'
                    },
                    {
                        title: 'Speak to an expert',
                        link: '/contact',
                        icon: 'ic:round-keyboard-arrow-right',
                    },
                ]" />

            <div class="w-full flex flex-col items-center gap-4">
                <!-- Architecture comparison -->
                <section class="max-w-screen-2xl w-full py-12 px-6">
                    <div class="grid lg:grid-cols-2 gap-6">
                        <img src="/images/technology-competitors-architecture.svg"
                            alt="Competitor architecture: data is synced into a database every 3-24 hours before reaching the customer server"
                            class="w-full h-auto rounded-lg border border-neutral-800" />
                        <img src="/images/technology-unified-architecture.svg"
                            alt="Unified.to architecture: real-time direct passthrough API calls, plus virtual and native webhooks"
                            class="w-full h-auto rounded-lg border border-neutral-800" />
                    </div>
                </section>

                <!-- Technology Comparison Table -->
                <section class="max-w-screen-2xl mx-auto w-full py-12 px-6">
                    <div class="mb-12 text-center">
                        <h2 class="text-4xl font-bold text-white mb-6">Unified.to versus Unified API, iPaaS, and ETL
                            solutions</h2>
                        <p class="text-neutral-50 text-xl max-w-3xl mx-auto">Discover the differences between
                            integration
                            solutions and how Unified.to outpaces alternatives in real-time data access, launch speed,
                            maintenance, and AI usefulness.</p>
                    </div>
                    <ComparisonTable :columns="TECHNOLOGY_COLUMNS" :sections="TECHNOLOGY_COMPARISON"
                        :highlight-column-index="3" :is-text-left-aligned="true" />
                </section>

                <!-- Two Column Section -->
                <section class="max-w-screen-lg mx-auto w-full py-12 px-6">
                    <div class="grid md:grid-cols-2 gap-8 items-center">
                        <div class="space-y-6">
                            <h2 class="text-4xl font-bold text-white">Modernizing integrations</h2>
                            <p class="text-neutral-50">Unified.to redefines how engineers test, launch, and maintain
                                integrations, with unparalleled developer ease, use case support, and scalability. All
                                the
                                data we transmit from your customers' third-party accounts is clean, normalized, and
                                real-time, ideal for elevating AI and SaaS applications.</p>
                            <ul class="text-gray-400 space-y-2">
                                <li>&middot; Native and virtual webhooks for real-time updates</li>
                                <li>&middot; Modern SDKs for Python, Typescript, Go, and more</li>
                                <li>&middot; Centralized observability and logging</li>
                            </ul>
                            <a href="https://docs.unified.to" class="text-blue-500 flex items-center gap-2">
                                Explore docs
                                <Icon name="ic:round-keyboard-arrow-right" />
                            </a>
                        </div>
                        <div>
                            <img src="/images/Finalized_lego.png"
                                alt="Real-time Unified API is not like other Unified APIs"
                                class="w-full h-auto object-contain max-h-80" />
                        </div>
                    </div>
                </section>

                <section>
                    <Benefits title="The real-time data layer for AI SaaS applications"
                        subtitle="Gain a powerful integration partner to accelerate integrations, secure more customers, and expand your product."
                        bottom_image :benefits="[
                            {
                                title: TOTAL_INTEGRATIONS + ' integrations',
                                body: 'Hundreds of pre-built integrations with deep support, ready to activate in minutes.',
                                image_url: '/images/integrations.svg',

                            },
                            {
                                title: `${Object.keys(INTEGRATION_CATEGORIES).length}+ API categories`,
                                body: 'Find integrations for your target industry, from ATS to Accounting and CRM.',
                                image_url: '/images/categories.svg',

                            },
                            {
                                title: '5/5 Reviews',
                                body: 'Our customers rate us highly for ease of use, integration depth, and support.',
                                image_url: '/images/reviews.svg',

                            },
                        ]" />
                </section>

                <section class="pt-24 max-w-full">
                    <Customers
                        title="Trusted by global product companies and developers to power integrations for innovative AI and SaaS applications. " />
                </section>

                <section class="-mt-40">
                    <Testimonials :delay="6000" :limit="6" title="" />
                </section>
            </div>

            <p class="mt-12 text-center text-xs text-gray-500">Current as of February 2025</p>

            <Hero title="Integrate. Faster." :highlight-words="['faster.']" :buttons="[
                { link: 'https://app.unified.to', title: 'Get started free' },
                { link: '/contact', title: 'Speak to an expert', icon: 'ic:round-keyboard-arrow-right' },
            ]" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { TECHNOLOGY_COLUMNS, TECHNOLOGY_COMPARISON } from '~/constants/technology-comparison';
import { TOTAL_INTEGRATIONS } from '~/constants/integrations';
import { INTEGRATION_CATEGORIES } from '~/services/integrations';

useSEO({
    title: 'Unified Technology',
    description:
        'See how Unified.to compares to ETL, iPaaS, and first-generation unified APIs. Real-time data, complete unification, and zero maintenance for modern SaaS and AI applications.',
});
</script>

<style>
/* Add any page-specific styles here */
</style>